/* ==========================================================================
   Collection filters – filter drawer, quick filters, usage events
   Loaded by snippets/collection-filters.liquid.

   Filtering itself stays in facets.js: FacetFiltersForm reads the form, updates
   the URL and re-renders the grid. This file opens and closes the drawer, sends
   quick-filter clicks through FacetFiltersForm, and reports filter usage.
   ========================================================================== */

/* ---------- CollectionFilterEvents ---------- */

const CollectionFilterEvents = {
  track(eventName, params = {}) {
    const root = document.querySelector('collection-filters');
    const payload = { collection_handle: root?.dataset.collectionHandle || '', ...params };

    // Other listeners (GTM custom HTML, Hotjar, tests) can hook in here without a second send.
    document.dispatchEvent(new CustomEvent('collection-filters:track', { detail: { event: eventName, ...payload } }));

    if (typeof gtag === 'function') gtag('event', eventName, payload);
  },

  productCount() {
    const count = parseInt(document.getElementById('product-grid')?.dataset.productsCount, 10);
    return Number.isNaN(count) ? undefined : count;
  },

  // Resolves once facets.js finishes its next render. Times out so a failed fetch
  // can't hold an event back forever.
  nextRender(timeout = 6000) {
    return new Promise((resolve) => {
      let timer;
      const unsubscribe = subscribe(PUB_SUB_EVENTS.facetUpdate, () => {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      });
      timer = setTimeout(() => {
        unsubscribe();
        resolve();
      }, timeout);
    });
  },

  // Filter params only, sorted and without empty price fields, so two states compare cleanly.
  filterKey(params) {
    const filters = new URLSearchParams();
    new URLSearchParams(params).forEach((value, key) => {
      if (key.startsWith('filter.') && value !== '') filters.append(key, value);
    });
    filters.sort();
    return filters.toString();
  },

  urlFilterKey() {
    return this.filterKey(window.location.search);
  },

  // The filters in the URL as short GA4-friendly strings, e.g. "shape:aviator,fit:oversized".
  appliedFilters() {
    const groups = new Set();
    const values = [];
    const inputs = Array.from(document.querySelectorAll('#FacetFiltersFormMobile input[name^="filter."]'));

    new URLSearchParams(this.urlFilterKey()).forEach((value, key) => {
      if (key.startsWith('filter.v.price')) {
        groups.add('price');
        return;
      }
      const input = inputs.find((element) => element.name === key && element.value === value);
      const group = input?.dataset.filterGroup || key.split('.').pop();
      groups.add(group);
      values.push(`${group}:${input?.dataset.filterValue || value}`);
    });

    return {
      filter_group: Array.from(groups).join(',').slice(0, 100),
      filter_value: values.join(',').slice(0, 100),
      filter_count: values.length + (groups.has('price') ? 1 : 0),
    };
  },

  isRendering() {
    return typeof FacetFiltersForm !== 'undefined' && Boolean(FacetFiltersForm.renderInProgress);
  },
};

/* ---------- <filter-drawer> ---------- */

class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.filter-drawer__panel');
    this.form = this.closest('form');
    this.onKeyDown = this.onKeyDown.bind(this);

    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((toggle) => {
      toggle.addEventListener('click', () => this.open(toggle));
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-drawer-close]')) this.close();
    });

    // Filters apply as they change, so Apply (or Enter in a price field) only closes the drawer.
    this.form?.addEventListener('submit', (event) => {
      if (!customElements.get('facet-filters-form')) return;
      event.preventDefault();
      this.close();
    });
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open(toggle) {
    if (this.isOpen) return;

    this.toggle = toggle;
    this.scrollPosition = window.scrollY;
    this.filtersOnOpen = CollectionFilterEvents.urlFilterKey();

    // Hiding the page scrollbar would widen the page and shift the grid; pad it back.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.body.classList.add('overflow-hidden');

    this.classList.add('is-open');
    toggle?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onKeyDown);
    trapFocus(this.panel, this.querySelector('.filter-drawer__close'));

    CollectionFilterEvents.track('collection_filter_open', {
      ...CollectionFilterEvents.appliedFilters(),
      product_count: CollectionFilterEvents.productCount(),
    });
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onKeyDown);
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';
    removeTrapFocus();
    this.toggle?.focus({ preventScroll: true });
    if (window.scrollY !== this.scrollPosition) window.scrollTo(0, this.scrollPosition);

    this.afterClose();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  // Waits for the last change to render. If the results changed while the shopper was further
  // down the page than the toolbar, the new results are shown from the top; then the apply is reported.
  async afterClose() {
    const pending = CollectionFilterEvents.filterKey(new FormData(this.form)) !== CollectionFilterEvents.urlFilterKey();
    if (pending || CollectionFilterEvents.isRendering()) await CollectionFilterEvents.nextRender();

    if (CollectionFilterEvents.urlFilterKey() === this.filtersOnOpen) return;

    if (!this.isOpen) {
      const header = document.querySelector('.shopify-section-header-sticky');
      const toolbarTop = this.form.getBoundingClientRect().top + window.scrollY - (header ? header.offsetHeight : 0);
      if (window.scrollY > toolbarTop) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(toolbarTop, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    CollectionFilterEvents.track('collection_filter_apply', {
      ...CollectionFilterEvents.appliedFilters(),
      product_count: CollectionFilterEvents.productCount(),
    });
    this.filtersOnOpen = CollectionFilterEvents.urlFilterKey();
  }

  // A clear inside the open drawer is reported on its own, so closing afterwards isn't also an apply.
  resetSnapshot() {
    this.filtersOnOpen = CollectionFilterEvents.urlFilterKey();
  }
}

/* ---------- <collection-filters> ---------- */

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    // Capture phase: facet-remove starts the re-render in its own handler, and a cached render
    // finishes synchronously, so start listening for it before that handler runs.
    this.addEventListener('click', this.onRemove.bind(this), true);
    this.addEventListener(
      'keyup',
      (event) => {
        if (event.code === 'Space') this.onRemove(event);
      },
      true
    );
    this.addEventListener('change', this.onSortChange.bind(this));
  }

  onRemove(event) {
    const link = event.target.closest('a[data-filter-action="remove"], a[data-filter-action="clear"]');
    if (!link) return;

    const params =
      link.dataset.filterAction === 'remove'
        ? { clear_type: 'single', filter_group: link.dataset.filterGroup, filter_value: link.dataset.filterValue }
        : { clear_type: 'all', ...CollectionFilterEvents.appliedFilters() };

    CollectionFilterEvents.nextRender().then(() => {
      this.querySelectorAll('filter-drawer').forEach((drawer) => {
        if (drawer.isOpen) drawer.resetSnapshot();
      });
      CollectionFilterEvents.track('collection_filter_clear', {
        ...params,
        product_count: CollectionFilterEvents.productCount(),
      });
    });
  }

  // Several quick changes share one render, so they're reported once, with the last choice.
  onSortChange(event) {
    if (!event.target.matches('[data-sort-select]')) return;

    this.latestSort = event.target.value;
    if (this.sortPending) return;

    this.sortPending = true;
    CollectionFilterEvents.nextRender().then(() => {
      this.sortPending = false;
      CollectionFilterEvents.track('collection_sort_change', {
        sort_by: this.latestSort,
        product_count: CollectionFilterEvents.productCount(),
      });
    });
  }
}

/* ---------- <collection-quick-filters> ---------- */

class CollectionQuickFilters extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.revealActive();
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.facetUpdate, () => this.revealActive());
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  onClick(event) {
    const link = event.target.closest('a[data-quick-filter]');
    if (!link) return;

    const isFilter = link.dataset.quickFilterType === 'filter';
    const params = {
      quick_filter: link.dataset.quickFilter,
      filter_group: isFilter ? link.dataset.filterGroup : 'collection',
      filter_value: isFilter ? link.dataset.filterValue : link.dataset.quickFilter,
    };
    if (isFilter) params.filter_state = link.getAttribute('aria-current') === 'true' ? 'removed' : 'added';

    const opensElsewhere = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    if (!isFilter || opensElsewhere || typeof FacetFiltersForm === 'undefined') {
      // The link loads a page, so report now rather than after a render.
      CollectionFilterEvents.track('collection_quick_filter_click', params);
      return;
    }

    event.preventDefault();
    CollectionFilterEvents.nextRender().then(() => {
      CollectionFilterEvents.track('collection_quick_filter_click', {
        ...params,
        product_count: CollectionFilterEvents.productCount(),
      });
    });
    FacetFiltersForm.renderPage(new URL(link.href).searchParams.toString());
  }

  // Keeps the selected quick filter in view when the strip scrolls sideways.
  revealActive() {
    const active = this.querySelector('[aria-current]');
    if (!active || this.scrollWidth <= this.clientWidth) return;

    const strip = this.getBoundingClientRect();
    const chip = active.getBoundingClientRect();
    if (chip.left >= strip.left && chip.right <= strip.right) return;

    this.scrollLeft += chip.left - strip.left - (strip.width - chip.width) / 2;
  }
}

if (!customElements.get('filter-drawer')) customElements.define('filter-drawer', FilterDrawer);
if (!customElements.get('collection-filters')) customElements.define('collection-filters', CollectionFilters);
if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', CollectionQuickFilters);
}
